import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useReducedMotion, REDUCED_MOTION_FADE } from "@/lib/motion";

import fobFrame from "@/assets/keyring-fob-frame-landscape.png";
import windowMask from "@/assets/keyring-window-mask-landscape.png";

/**
 * KeyringFob — "novelty car keyring" rendering of the booking confirmation
 * car image. Used on both the live confirmation screen (`/bookings/:publicId`)
 * and the design preview (`/bookings/preview-v2`); it has fully replaced the
 * rounded photo card on the live path.
 *
 * The fob itself is a finished, transparent PNG mockup (`keyring-fob-frame`):
 * a black leather card-holder with a chrome split-ring and two real car keys
 * threaded on top, and a rectangular cut-out window. We composite the clean
 * car photo BEHIND the frame so it shows through that window — no text, badges
 * or stamps are overlaid on the image.
 *
 * The photo lives inside a container masked by `keyring-window-mask` — a
 * derived alpha mask that is opaque only over the exact window opening. This
 * clips the photo precisely to the window shape (with the real rounded
 * corners), so it can never spill into the frame's transparent outer area no
 * matter how generously the photo is bled / tilted to guarantee full coverage.
 */

/**
 * Photo backing plate — the landscape assets are the original approved
 * portrait fob artwork rotated 90° clockwise (keys now threaded on the
 * right). The portrait plate (left -1.5%, top 23.7%, 89.4% × 77.8%) maps
 * under that rotation to the constants below: still centred on the window
 * centre and blown out to ~1.25x the opening on every side, so the plate can
 * be tilted inside the frame without ever revealing an edge; the mask trims
 * the overspill back to the exact opening.
 */
const PHOTO = {
  left: "-1.5%",
  top: "-1.5%",
  width: "77.8%",
  height: "89.4%",
} as const;

/** Resting slant of the whole fob (deg). Negative = keys up to the top-right,
 *  matching the approved landscape mockup where the keys drape down the right
 *  edge from a raised corner. */
const REST_TILT = -9;

/** Extra tilt of the car photo *inside* the window opening (deg). Unchanged
 *  by the 90° asset rotation (relative angles are preserved). */
const PHOTO_TILT = -5;

interface KeyringFobProps {
  image: string;
  carName: string;
}

/** Pick the in-window zoom for a photo of the given natural aspect (w/h).
 *  The window opening is ~1.20 (mask's opaque bounding box): `object-cover`
 *  into that squarer window already discards a lot of a wide 16:9 photo's
 *  width (only ~68% survives), so zooming those further routinely cut real
 *  cars off (spot-checked across ~20 live listing heroes, Task #6561).
 *  Photos whose aspect is CLOSE to the window's lose almost nothing to
 *  cover-cropping and can afford a gentle zoom to fill the window with the
 *  car instead of background.
 *
 *  Wide (>1.45, e.g. 16:9) and square/portrait (≤1.05) photos are already
 *  heavily cover-cropped by the window — no extra zoom, or the car gets cut
 *  off. Near-window aspects (4:3, 3:2) take a light 1.1x. */
export function zoomForAspect(aspect: number | null): number {
  if (aspect == null || !Number.isFinite(aspect) || aspect <= 0) return 1.1;
  if (aspect > 1.45 || aspect <= 1.05) return 1.0;
  return 1.1;
}

export function KeyringFob({ image, carName }: KeyringFobProps) {
  // Gate the swing entrance on image readiness so the fob doesn't animate in
  // while the car photo / frame are still decoding. The same preload measures
  // the car photo's natural aspect so the crop can adapt to it.
  const [ready, setReady] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [photoAspect, setPhotoAspect] = useState<number | null>(null);
  const reduced = useReducedMotion();
  useEffect(() => {
    let cancelled = false;
    setPhotoAspect(null);
    const srcs = image ? [image, fobFrame] : [fobFrame];
    let remaining = srcs.length;
    const done = () => {
      if (cancelled) return;
      remaining -= 1;
      if (remaining <= 0) setReady(true);
    };
    srcs.forEach((src) => {
      const img = new Image();
      let settled = false;
      const settle = () => {
        if (settled) return;
        settled = true;
        if (!cancelled && src === image && img.naturalWidth > 0 && img.naturalHeight > 0) {
          setPhotoAspect(img.naturalWidth / img.naturalHeight);
        }
        done();
      };
      img.onload = settle;
      img.onerror = settle;
      img.src = src;
      if (img.complete) settle();
    });
    return () => {
      cancelled = true;
    };
  }, [image]);

  const maskStyle = {
    WebkitMaskImage: `url(${windowMask})`,
    maskImage: `url(${windowMask})`,
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
  } as const;

  return (
    <div className="flex w-full justify-center py-2 max-md:px-4" data-testid="keyring-fob">
      <motion.div
        className="relative w-full max-w-[520px] sm:max-w-[580px]"
        style={{ transformOrigin: "90% 44%" }}
        initial={reduced ? { opacity: 0, rotate: REST_TILT } : { opacity: 0, rotate: REST_TILT - 14, y: -12 }}
        animate={
          ready
            ? { opacity: 1, rotate: REST_TILT, y: 0 }
            : reduced
            ? { opacity: 0, rotate: REST_TILT }
            : { opacity: 0, rotate: REST_TILT - 14, y: -12 }
        }
        transition={
          reduced
            ? REDUCED_MOTION_FADE
            : {
                opacity: { duration: 0.5, ease: "easeOut" },
                rotate: { type: "spring", stiffness: 130, damping: 9, mass: 0.9 },
                y: { type: "spring", stiffness: 130, damping: 11 },
              }
        }
      >
        {/* Clean car photo, clipped to the exact window opening by the derived
            alpha mask. Nothing is overlaid on the image. */}
        <div className="absolute inset-0" style={maskStyle}>
          {/* Car photo, generously bled past the opening and tilted inside the
              frame; the mask trims the overspill. */}
          <div
            className="absolute"
            style={{
              left: PHOTO.left,
              top: PHOTO.top,
              width: PHOTO.width,
              height: PHOTO.height,
              transform: `rotate(${PHOTO_TILT}deg)`,
            }}
          >
            <div className="relative h-full w-full bg-brand-deep">
              {image && (
                <img
                  src={image}
                  alt={carName}
                  draggable={false}
                  className="h-full w-full select-none object-cover"
                  style={{
                    // Aspect-aware zoom (Task #6561): wide 16:9 and
                    // square/portrait photos are shown without extra zoom —
                    // the window's cover-crop already trims them hard and any
                    // more cut real cars off. Near-window aspects get a light
                    // 1.1x, biased slightly below centre so the car (not the
                    // sky) fills the opening.
                    transform: `scale(${zoomForAspect(photoAspect)})`,
                    transformOrigin: "50% 62%",
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* The finished fob frame (leather + bezel + threaded car keys) on top;
            its transparent window reveals the masked photo beneath. */}
        <img
          src={fobFrame}
          alt=""
          aria-hidden="true"
          draggable={false}
          onLoad={() => setFrameLoaded(true)}
          onError={() => setFrameLoaded(false)}
          className={`pointer-events-none relative block w-full select-none${frameLoaded ? " drop-shadow-[0_18px_30px_rgba(0,0,0,0.32)]" : ""}`}
        />
      </motion.div>
    </div>
  );
}

export default KeyringFob;
